/**
 * Profile Image API service
 * Handles HTTP requests for profile image upload flow
 * Two-step process: (1) get presigned upload URL, (2) confirm upload
 */

import { api } from '@/lib/api';

/** Response from GET /users/me/profile-image-upload-url */
export interface UploadUrlResponse {
  /** S3 bucket URL for the multipart/form-data POST upload (expires in 5 minutes) */
  url: string;
  /** Form fields that must be included in the multipart/form-data POST before the file.
   * Contains policy, signature, and other S3 presigned POST fields. */
  fields: Record<string, string>;
  /** S3 object key - must be passed to confirmUpload after successful S3 upload */
  imageKey: string;
  /** Seconds until the presigned URL expires */
  expiresIn: number;
}

/** Response from PUT /users/me/profile-image */
export interface ConfirmUploadResponse {
  /** Full S3 URL of the stored profile image */
  profileImageUrl: string;
  /** Success confirmation message */
  message: string;
}

/**
 * Get a presigned S3 URL for uploading a profile image
 * @param contentType - MIME type of the image (image/jpeg, image/png, or image/webp)
 * @returns Promise resolving to UploadUrlResponse with presigned POST url, fields, and image key
 * @throws AuthError if contentType is invalid (400), user not authenticated (401), or server error (500)
 */
export async function getUploadUrl(contentType: string): Promise<UploadUrlResponse> {
  const response = await api.get<UploadUrlResponse>('/users/me/profile-image-upload-url', {
    params: { contentType },
  });
  return response.data;
}

/**
 * Confirm that a profile image has been uploaded to S3
 * Called after successful direct-to-S3 upload using presigned URL
 * @param imageKey - S3 object key returned from getUploadUrl
 * @returns Promise resolving to ConfirmUploadResponse with the stored profile image URL
 * @throws AuthError if imageKey is invalid (400), not authenticated (401), wrong user (403), or server error (500)
 */
export async function confirmUpload(imageKey: string): Promise<ConfirmUploadResponse> {
  const response = await api.put<ConfirmUploadResponse>('/users/me/profile-image', {
    imageKey,
  });
  return response.data;
}
